import React, { useEffect, useRef, useState } from "react";
import {
    BackspaceRounded,
    CheckCircleRounded,
    WhatsApp,
} from "@mui/icons-material";
import {
    Box,
    GlobalStyles,
    IconButton,
    Typography,
} from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";
import axios from "axios";
import PrimaryButton from "../Reusables/PrimaryButton";
import PaymentModal, {
    getOrderDisplayNumber,
    printAcknowledgementInvoice,
} from "./PaymentModal";
import { API } from "../../utils/Api";
import { useNavigate } from "react-router-dom";


const keypadNumbers = [
    "1",
    "2",
    "3",
    "4",
    "5",
    "6",
    "7",
    "8",
    "9",
];

const formatAmount = (value) => Number(value || 0).toFixed(2);

const formatReadyTime = (date) => {
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");

    return `${hours}:${minutes}`;
};

const calculateMakingTimeFromCart = (cartItems = []) => {
    const maxMakingTime = cartItems.reduce((max, cartItem) => {
        const itemMakingTime = Number(
            cartItem?.makingTime ??
            cartItem?.productMakingTime ??
            0
        );

        return Math.max(max, itemMakingTime);
    }, 0);

    const bufferMinutes = Number(API.ORDER_MAKING_TIME_BUFFER || 5);
    const totalMakingTime = maxMakingTime + bufferMinutes;
    const readyDate = new Date();
    readyDate.setMinutes(
        readyDate.getMinutes() + totalMakingTime
    );

    const makingTimeDuration = `${totalMakingTime} minutes`;

    return {
        makingTimeDuration,
        readyTime: formatReadyTime(readyDate),
        arrivalTimeDurationInMinutes: totalMakingTime,
    };
};

const mapPickupOption = (orderType) => {
    const normalized = String(orderType || "")
        .toUpperCase()
        .replace(/_/g, "-");

    if (normalized === "TAKEAWAY" || normalized === "TAKE-AWAY") {
        return "TAKE-AWAY";
    }

    return "DINE-IN";
};

const mapPaymentMode = (paymentMode) => {
    if (!paymentMode) {
        return "ONLINE";
    }

    const normalized = String(paymentMode).toUpperCase();

    if (normalized === "UPI" || normalized === "CARD") {
        return "ONLINE";
    }

    return normalized;
};

const createTransactionNumber = (prefix = "XP") =>
    `${prefix}${Date.now()}${Math.floor(Math.random() * 1000)
        .toString()
        .padStart(3, "0")}`;

const extractPlutusReferenceId = (result) =>
    result?.PlutusTransactionReferenceID ??
    result?.plutusTransactionReferenceID ??
    result?.data?.PlutusTransactionReferenceID ??
    result?.data?.plutusTransactionReferenceID ??
    result?.data?.data?.PlutusTransactionReferenceID ??
    result?.data?.data?.plutusTransactionReferenceID ??
    null;

/** Pine Labs AllowedPaymentMode: 1 = Card, 10 = UPI */
const mapAllowedPaymentMode = (paymentMode) => {
    const normalized = String(paymentMode || "")
        .toUpperCase()
        .trim();

    if (normalized === "CARD") {
        return "1";
    }

    return "10";
};

const PaymentProcessing = ({
    embedded = false,
    cart = [],
    outletData = null,
    orderType,
    paymentMode,
    subtotal = 0,
    taxAmount = 0,
    discountAmount = 0,
    grandTotal = 0,
    token,
    cartProductRawMaterials: checkedCartProductRawMaterials = [],
    isCreatingOrder = false,
    setIsCreatingOrder,
    onOrderCreated,
    onOrderError,
}) => {
    const navigate = useNavigate();
    const [phone, setPhone] = useState("");
    const [error, setError] = useState("");
    const [isProceeding, setIsProceeding] = useState(false);
    const isLoading = isProceeding || isCreatingOrder;
    const [shake, setShake] = useState(false);
    const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
    const activePaymentRef = useRef(null);

    useEffect(() => {
        return () => {
            cancelPineLabsPayment(activePaymentRef.current);
        };
    }, []);

    const handleNumberClick = (number) => {
        if (isLoading || phone.length >= 10) {
            return;
        }

        setPhone((previousPhone) => `${previousPhone}${number}`);
        setError("");
    };

    const handleBackspace = () => {
        if (isLoading || !phone.length) {
            return;
        }

        setPhone((previousPhone) => previousPhone.slice(0, -1));
        setError("");
    };

    const handleClear = () => {
        if (isLoading) {
            return;
        }

        setPhone("");
        setError("");
    };

    const validatePhone = () => {
        let phoneError = "";

        if (!phone) {
            phoneError = "Mobile number is required.";
        } else if (!/^\d{10}$/.test(phone)) {
            phoneError =
                "Please enter a valid 10-digit mobile number.";
        }

        setError(phoneError);

        if (phoneError) {
            setShake(true);

            setTimeout(() => {
                setShake(false);
            }, 450);

            return false;
        }

        return true;
    };

    const handleProceed = async () => {
        if (!validatePhone() || isLoading) {
            return;
        }

        setIsProceeding(true);
        setError("");

        try {
            sessionStorage.setItem("userPhone", phone);
            sessionStorage.removeItem("phoneSkipped");
            setIsConfirmationModalOpen(true);
            await initiatePineLabsPayment();
        } catch (apiError) {
            console.error("Payment initiation error:", apiError);
            const message =
                apiError?.message ||
                "Unable to initiate payment. Please try again.";
            setError(message);
            onOrderError?.(message);
            setIsConfirmationModalOpen(false);
        } finally {
            setIsProceeding(false);
        }
    };

    const handleSkip = async () => {
        if (isLoading) {
            return;
        }

        setPhone("");
        setError("");
        setIsProceeding(true);

        try {
            sessionStorage.removeItem("userPhone");
            sessionStorage.setItem("phoneSkipped", "true");
            setIsConfirmationModalOpen(true);
            await initiatePineLabsPayment();
        } catch (apiError) {
            console.error("Payment initiation error:", apiError);
            const message =
                apiError?.message ||
                "Unable to initiate payment. Please try again.";
            setError(message);
            onOrderError?.(message);
            setIsConfirmationModalOpen(false);
        } finally {
            setIsProceeding(false);
        }
    };

    const roundAmount = (value) => {
        return Number(Number(value || 0).toFixed(2));
    };

    const getMongoId = (value) => {
        if (!value) {
            return undefined;
        }

        if (typeof value === "object") {
            return value._id || value.id || undefined;
        }

        return value;
    };

    const removeUndefinedValues = (object) => {
        return Object.fromEntries(
            Object.entries(object).filter(
                ([, value]) => value !== undefined
            )
        );
    };

    const buildOrderPayload = () => {
        const validMobileNumber =
            phone && /^\d{10}$/.test(phone)
                ? phone
                : undefined;

        const {
            makingTimeDuration,
            readyTime,
            arrivalTimeDurationInMinutes,
        } = calculateMakingTimeFromCart(cart);

        /*
         * Generate customerCartItemsData based on the new payload.
         */
        const customerCartItemsData = cart.map((cartItem) => {
            const quantity = Number(cartItem.quantity || 1);

            const productRate = Number(
                cartItem.productPrice ??
                cartItem.rate ??
                cartItem.price ??
                cartItem.offerPrice ??
                0
            );

            const addons = Array.isArray(cartItem.addons)
                ? cartItem.addons
                    .map((addon) => {
                        const addonId =
                            getMongoId(addon.addonId) ||
                            getMongoId(addon.addonAllocationId);

                        const selectedValueId =
                            getMongoId(addon.selectedValueId) ||
                            getMongoId(addon.valueId) ||
                            getMongoId(addon.addonValueId) ||
                            getMongoId(addon._id);

                        const selectedValue =
                            addon.selectedValue ??
                            addon.label ??
                            addon.value ??
                            addon.addonTitle ??
                            addon.name ??
                            "";

                        const price = Number(
                            addon.price ??
                            addon.offerPrice ??
                            0
                        );

                        return removeUndefinedValues({
                            _id: addonId || selectedValueId,
                            selectedValue:
                                typeof selectedValue === "string"
                                    ? selectedValue
                                    : selectedValue?.value ||
                                      selectedValue?.name ||
                                      "",
                            price: roundAmount(price),
                            selectedValueId,
                        });
                    })
                    .filter((addon) => addon._id)
                : [];

            const addonsTotalPerUnit = addons.reduce(
                (total, addon) =>
                    total + Number(addon.price || 0),
                0
            );

            const productMrp = Number(
                cartItem.productMrp ??
                cartItem.mrp ??
                cartItem.newMrp ??
                0
            );

            /*
             * Backend validates rate / mrpRate against product prices only.
             * Addon prices are sent on addons[] and rolled into taxable/grandTotal.
             */
            const rate = roundAmount(
                Number(
                    productRate > 0
                        ? productRate
                        : cartItem.rate ?? 0
                )
            );

            const mrpRate = roundAmount(
                Number(
                    productMrp > 0
                        ? productMrp
                        : cartItem.mrpRate ?? rate
                )
            );

            const unitPriceWithAddons = roundAmount(
                Number(
                    cartItem.unitPrice ??
                    rate + addonsTotalPerUnit
                )
            );

            const taxable = roundAmount(
                Number(
                    cartItem.taxable ??
                    unitPriceWithAddons * quantity
                )
            );

            const itemTaxAmount = roundAmount(
                Number(
                    cartItem.taxAmount ??
                    cartItem.totalTaxAmount ??
                    0
                )
            );

            const grandTotal = roundAmount(
                Number(
                    cartItem.grandTotal ??
                    cartItem.totalPrice ??
                    taxable + itemTaxAmount
                )
            );

            const productId =
                getMongoId(cartItem.productId) ||
                getMongoId(cartItem.product);

            return removeUndefinedValues({
                quantity,
                mrpRate,
                taxable,
                taxAmount: itemTaxAmount,
                grandTotal,
                sourceType: "QUICK-SERVE",
                extraAddonsInfo:
                    cartItem.extraAddonsInfo ?? null,
                product: productId,
                addons,
                rate,
            });
        });

        /*
         * Prefer cartProductRawMaterials from the stock-check API response.
         * Fall back to per-cart-item raw material fields when unavailable.
         */
        const formatRawMaterialEntry = (
            rawMaterial,
            cartItem = null
        ) => {
            const cartProductId = cartItem
                ? getMongoId(cartItem.productId) ||
                  getMongoId(cartItem.product)
                : undefined;

            const productId =
                getMongoId(rawMaterial.product) ||
                cartProductId;

            const rawMaterialId =
                getMongoId(rawMaterial.rawMaterial) ||
                getMongoId(rawMaterial.rawMaterialId);

            const recipeInfo =
                rawMaterial.recipeInfo || null;

            const formattedRecipeInfo = recipeInfo
                ? removeUndefinedValues({
                    addonId:
                        getMongoId(
                            recipeInfo.addonId
                        ) ?? null,

                    addonValueId:
                        getMongoId(
                            recipeInfo.addonValueId
                        ) ?? null,

                    isImportant: Boolean(
                        recipeInfo.isImportant
                    ),

                    isOptional: Boolean(
                        recipeInfo.isOptional
                    ),

                    quantity: Number(
                        recipeInfo.quantity ??
                        rawMaterial.debitQuantity ??
                        rawMaterial.quantity ??
                        0
                    ),
                })
                : undefined;

            return removeUndefinedValues({
                _id:
                    getMongoId(rawMaterial._id) ||
                    undefined,

                product: productId,

                rawMaterial: rawMaterialId,

                productName:
                    rawMaterial.productName ??
                    cartItem?.productName ??
                    cartItem?.name ??
                    cartItem?.title ??
                    "",

                rawMaterialName:
                    rawMaterial.rawMaterialName ??
                    rawMaterial.name ??
                    rawMaterial.materialName ??
                    "",

                debitQuantity: Number(
                    rawMaterial.debitQuantity ??
                    rawMaterial.quantity ??
                    0
                ),

                balanceStock: Number(
                    rawMaterial.balanceStock ?? 0
                ),

                recipeInfo: formattedRecipeInfo,
            });
        };

        const cartProductRawMaterials =
            Array.isArray(checkedCartProductRawMaterials) &&
            checkedCartProductRawMaterials.length > 0
                ? checkedCartProductRawMaterials.map(
                    (rawMaterial) =>
                        formatRawMaterialEntry(rawMaterial)
                )
                : cart.flatMap((cartItem) => {
                    const rawMaterials =
                        cartItem.cartProductRawMaterials ??
                        cartItem.productRawMaterials ??
                        cartItem.rawMaterials ??
                        [];

                    if (!Array.isArray(rawMaterials)) {
                        return [];
                    }

                    return rawMaterials.map((rawMaterial) =>
                        formatRawMaterialEntry(
                            rawMaterial,
                            cartItem
                        )
                    );
                });

        /*
         * Cart totals must equal the sum of line items.
         * Preferring UI props here previously caused mismatches when
         * item.rate omitted addons but cartSummary.taxable included them.
         */
        const finalTotalTaxable = customerCartItemsData.reduce(
            (total, item) => total + Number(item.taxable || 0),
            0
        );

        const finalTaxAmount = customerCartItemsData.reduce(
            (total, item) => total + Number(item.taxAmount || 0),
            0
        );

        const finalGrandTotal = customerCartItemsData.reduce(
            (total, item) => total + Number(item.grandTotal || 0),
            0
        );

        const outletId = getMongoId(outletData);

        const menuGroupId =
            getMongoId(outletData?.menuGroup) ||
            getMongoId(outletData?.menuGroupId);

        const payload = {
            arrivalTimeDurationInMinutes,
            ordersType: "PICKUP",
            deliveryDetails: null,
            outlet: outletId,
            paymentMode: mapPaymentMode(paymentMode),
            readyTime,
            pickupOption: mapPickupOption(orderType),

            makingTimeDuration:
                makingTimeDuration ||
                `${arrivalTimeDurationInMinutes} minutes`,

            customerCartData: {
                totalTaxable: roundAmount(
                    finalTotalTaxable
                ),

                totalTaxAmount: roundAmount(
                    finalTaxAmount
                ),

                deliveryCharges: null,

                grandTotal: roundAmount(
                    finalGrandTotal
                ),

                menuGroup: menuGroupId,

                paymentInitiatedAt:
                    new Date().toISOString(),

                ordersType: "PICKUP",

                source: "QUICK-SERVE",

                outlet: outletId,

                cartProductRawMaterials,
            },

            customerCartItemsData,
        };

        /*
         * Do not send customerPhoneNumber when skipped.
         */
        if (validMobileNumber) {
            payload.customerPhoneNumber =
                validMobileNumber;
        }

        console.log(
            "Create order payload:",
            JSON.stringify(payload, null, 2)
        );

        return payload;
    };

    const handleConfirmPayment = async () => {
        if (isLoading) {
            return;
        }

        if (!outletData?._id) {
            const message = "Outlet information is missing.";
            setError(message);
            onOrderError?.(message);
            return;
        }

        if (!orderType) {
            const message = "Please select an order type.";
            setError(message);
            onOrderError?.(message);
            return;
        }

        if (!paymentMode) {
            const message = "Please select a payment mode.";
            setError(message);
            onOrderError?.(message);
            return;
        }

        if (!Array.isArray(cart) || cart.length === 0) {
            const message = "Cart is empty.";
            setError(message);
            onOrderError?.(message);
            return;
        }

        setIsCreatingOrder?.(true);
        setError("");

        try {
            const payload = buildOrderPayload();

            const response = await axios.post(
                `${API.BASE_URL}order/create-customer-order-POS`,
                payload,
                {
                    headers: {
                        "Content-Type": "application/json",
                        ...(token
                            ? {
                                Authorization: `Bearer ${token}`,
                            }
                            : {}),
                    },
                }
            );

            const createdOrder =
                response?.data?.data?.order ||
                response?.data?.data ||
                response?.data;

            if (phone && /^\d{10}$/.test(phone)) {
                sessionStorage.setItem("userPhone", phone);
                sessionStorage.removeItem("phoneSkipped");
            } else {
                sessionStorage.removeItem("userPhone");
                sessionStorage.setItem("phoneSkipped", "true");
            }

            activePaymentRef.current = null;
            setIsConfirmationModalOpen(false);

            const acknowledgementAmount =
                createdOrder?.grandTotal ??
                createdOrder?.order?.grandTotal ??
                grandTotal;

            printAcknowledgementInvoice({
                orderNumber: getOrderDisplayNumber(createdOrder),
                amount: acknowledgementAmount,
                outletName: outletData?.name || "Xpanse",
            });

            onOrderCreated?.(createdOrder);
            navigate(`/onlineOrders`);
        } catch (apiError) {
            console.error(
                "Create order API error:",
                apiError?.response?.data || apiError
            );

            const message =
                apiError?.response?.data?.message ||
                apiError?.response?.data?.error ||
                apiError?.message ||
                "Unable to create the order. Please try again.";

            setError(message);
            onOrderError?.(message);
        } finally {
            setIsCreatingOrder?.(false);
        }
    };

    const cancelPineLabsPayment = async (paymentSession = activePaymentRef.current) => {
        if (!paymentSession?.plutusTransactionReferenceID) {
            return;
        }

        const paymentToCancel = paymentSession;

        if (
            activePaymentRef.current?.plutusTransactionReferenceID ===
            paymentToCancel.plutusTransactionReferenceID
        ) {
            activePaymentRef.current = null;
        }

        try {
            const response = await fetch(
                `${API.BASE_URL}pinelab/cancel-payment-POS`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        PlutusTransactionReferenceID:
                            paymentToCancel.plutusTransactionReferenceID,
                        TransactionNumber:
                            paymentToCancel.transactionNumber,
                        Amount: paymentToCancel.amountInPaise,
                        UserID: "POS",
                        MerchantID:
                            outletData?.pineLabCredentials?.merchantId,
                        SecurityToken:
                            outletData?.pineLabCredentials?.securityToken,
                        ClientID: outletData?.pineLabCredentials?.clientId,
                        StoreID: outletData?.pineLabCredentials?.storeId,
                        outletId: outletData?._id,
                    }),
                }
            );

            const result = await response.json();

            if (!response.ok) {
                console.warn(
                    "Pine Labs cancel warning:",
                    result?.message || result
                );
            }
        } catch (apiError) {
            console.error("Pine Labs cancel error:", apiError);
        }
    };

    const initiatePineLabsPayment = async () => {
        await cancelPineLabsPayment();

        const allowedPaymentMode = mapAllowedPaymentMode(paymentMode);
        const transactionNumber = createTransactionNumber();
        const amountInPaise = Math.round(Number(grandTotal) * 100);

        const response = await fetch(
            `${API.BASE_URL}pinelab/initiate-payment-POS`,
            {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    TransactionNumber: transactionNumber,
                    SequenceNumber: "1",
                    AllowedPaymentMode: allowedPaymentMode,
                    Amount: amountInPaise,
                    UserID: "POS",
                    MerchantID: outletData?.pineLabCredentials?.merchantId,
                    SecurityToken:
                        outletData?.pineLabCredentials?.securityToken,
                    ClientID: outletData?.pineLabCredentials?.clientId,
                    StoreID: outletData?.pineLabCredentials?.storeId,
                    AutoCancelDurationInMinutes: 2,
                    outletId: outletData?._id,
                }),
            }
        );

        const result = await response.json();

        if (!response.ok) {
            throw new Error(
                result.message || "Payment initiation failed"
            );
        }

        const plutusTransactionReferenceID =
            extractPlutusReferenceId(result);

        if (plutusTransactionReferenceID) {
            activePaymentRef.current = {
                plutusTransactionReferenceID,
                transactionNumber,
                amountInPaise,
            };
        }

        console.log("Payment initiated:", result, {
            paymentMode,
            AllowedPaymentMode: allowedPaymentMode,
            transactionNumber,
            plutusTransactionReferenceID,
        });
        return result;
    };

    const handleDismissPaymentModal = async () => {
        if (isCreatingOrder) {
            return;
        }

        setIsConfirmationModalOpen(false);
        await cancelPineLabsPayment();
    };

    return (
        <>
            {!embedded && (
                <GlobalStyles
                    styles={{
                        html: {
                            width: "100%",
                            height: "100%",
                            overflow: "hidden",
                        },

                        body: {
                            width: "100%",
                            height: "100%",
                            margin: 0,
                            overflow: "hidden",
                            overscrollBehavior: "none",
                        },

                        "#root": {
                            width: "100%",
                            height: "100%",
                            overflow: "hidden",
                        },

                        "*": {
                            boxSizing: "border-box",
                        },

                        "@keyframes floatingShape": {
                            "0%, 100%": {
                                transform:
                                    "translate3d(0, 0, 0) rotate(0deg)",
                            },
                            "50%": {
                                transform:
                                    "translate3d(0, -14px, 0) rotate(4deg)",
                            },
                        },
                    }}
                />
            )}

            <Box
                sx={{
                    width: "100%",
                    height: "100%",
                    minHeight: 0,
                    position: "relative",
                    overflow: "hidden",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    p: embedded
                        ? 0
                        : {
                            xs: 1.5,
                            sm: 2.5,
                            md: 3,
                        },
                    background: "linear-gradient(145deg, #4fa9d5 0%, #80cdea 100%)",
                }}
            >
                {!embedded && <BackgroundDecorations />}

                <motion.div
                    initial={
                        embedded
                            ? false
                            : {
                                opacity: 0,
                                y: 30,
                                scale: 0.97,
                            }
                    }
                    animate={{
                        opacity: 1,
                        y: 0,
                        scale: 1,
                    }}
                    transition={{
                        duration: embedded ? 0 : 0.55,
                        ease: [0.22, 1, 0.36, 1],
                    }}
                    style={{
                        width: "100%",
                        height: "100%",
                        maxWidth: embedded ? "none" : "620px",
                        maxHeight: embedded ? "none" : "920px",
                        minHeight: 0,
                        position: "relative",
                        zIndex: 2,
                    }}
                >
                    <CombinedLoginSection
                        phone={phone}
                        error={error}
                        shake={shake}
                        isLoading={isLoading}
                        handleProceed={handleProceed}
                        handleNumberClick={handleNumberClick}
                        handleBackspace={handleBackspace}
                        handleClear={handleClear}
                        handleSkip={handleSkip}
                        embedded={embedded}
                    />
                </motion.div>
            </Box>

            <PaymentModal
                open={isConfirmationModalOpen}
                amount={grandTotal}
                isLoading={isLoading}
                onClose={handleDismissPaymentModal}
                onConfirm={handleConfirmPayment}
            />
        </>
    );
};

const CombinedLoginSection = ({
    phone,
    error,
    shake,
    isLoading,
    handleProceed,
    handleNumberClick,
    handleBackspace,
    handleClear,
    handleSkip,
    embedded,
}) => {
    return (
        <Box
            sx={{
                width: "100%",
                height: "100%",
                minHeight: 0,
                display: "flex",
                alignItems: embedded ? "stretch" : "center",
                justifyContent: "center",
                overflow: "hidden",
                p: {
                    xs: embedded ? 1 : 1.5,
                    sm: embedded ? 1.2 : 2,
                    md: embedded ? 1.4 : 3,
                },
            }}
        >
            <Box
                sx={{
                    position: "absolute",
                    top: "7%",
                    right: "8%",
                    width: "65px",
                    height: "65px",
                    borderRadius: "21px",
                    transform: "rotate(18deg)",
                    background: "rgba(255,255,255,0.12)",
                    animation:
                        "floatingShape 5s ease-in-out infinite",
                    pointerEvents: "none",
                }}
            />

            <motion.div
                initial={{
                    opacity: 0,
                    y: 25,
                    scale: 0.98,
                }}
                animate={{
                    opacity: 1,
                    y: 0,
                    scale: 1,
                }}
                transition={{
                    duration: 0.45,
                    ease: [0.22, 1, 0.36, 1],
                }}
                style={{
                    width: "100%",
                    maxWidth: embedded ? "100%" : "480px",
                    flex: embedded ? 1 : undefined,
                    height: embedded ? "auto" : "auto",
                    maxHeight: "100%",
                    minHeight: 0,
                    position: "relative",
                    zIndex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignSelf: "center",
                }}
            >
                <Box
                    sx={{
                        width: "100%",
                        flex: 1,
                        minHeight: 0,
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                        borderRadius: {
                            xs: "24px",
                            md: "28px",
                        },
                        backgroundColor: "#ffffff",
                        border:
                            "1px solid rgba(255,255,255,0.7)",
                        boxShadow:
                            "0 22px 55px rgba(19,76,110,0.22)",
                    }}
                >
                    <CombinedTopSection
                        phone={phone}
                        error={error}
                        shake={shake}
                        embedded={embedded}
                    />

                    <CombinedKeypad
                        phone={phone}
                        isLoading={isLoading}
                        handleProceed={handleProceed}
                        handleNumberClick={handleNumberClick}
                        handleBackspace={handleBackspace}
                        handleClear={handleClear}
                        handleSkip={handleSkip}
                        embedded={embedded}
                    />
                </Box>
            </motion.div>
        </Box>
    );
};

const CombinedTopSection = ({
    phone,
    error,
    shake,
    embedded,
}) => {
    return (
        <Box
            sx={{
                position: "relative",
                width: "100%",
                flexShrink: 0,
                px: {
                    xs: 1.2,
                    sm: 1.5,
                    md: 1.8,
                },
                pt: {
                    xs: 1.4,
                    md: 1.7,
                },
                pb: {
                    xs: 0.8,
                    md: 1,
                },
                overflow: "hidden",
                background:
                    "linear-gradient(160deg, #ffffff 0%, #f0f9fd 100%)",
            }}
        >
            <Box
                sx={{
                    position: "absolute",
                    top: "-90px",
                    right: "-80px",
                    width: "220px",
                    height: "220px",
                    borderRadius: "50%",
                    background: "rgba(111,190,229,0.12)",
                    pointerEvents: "none",
                }}
            />

            <Box sx={{ position: "relative", zIndex: 1 }}>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1.5,
                        mb: 1.1,
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.6,
                            px: 1,
                            py: 0.55,
                            borderRadius: "11px",
                            backgroundColor:
                                "rgba(36,199,106,0.1)",
                        }}
                    >
                        <WhatsApp
                            sx={{
                                color: "#24b967",
                                fontSize: "18px",
                            }}
                        />

                        <Typography
                            sx={{
                                color: "#52707f",
                                fontSize: {
                                    xs: "10px",
                                    sm: "12px",
                                },
                                fontWeight: 700,
                                whiteSpace: "nowrap",
                            }}
                        >
                            Invoice on WhatsApp
                        </Typography>
                    </Box>
                </Box>

                <Typography
                    component="h1"
                    sx={{
                        color: "#17364a",
                        fontSize: {
                            xs: "20px",
                            sm: "24px",
                            md: embedded ? "25px" : "28px",
                        },
                        fontWeight: 800,
                        lineHeight: 1.1,
                        letterSpacing: "-0.7px",
                        mb: 1.1,
                    }}
                >
                    Enter user's{" "}
                    <Box component="span" sx={{ color: "#54addb" }}>
                        mobile number
                    </Box>
                </Typography>

                <motion.div
                    animate={
                        shake
                            ? {
                                x: [-12, 12, -9, 9, -5, 5, 0],
                            }
                            : {}
                    }
                    transition={{ duration: 0.45 }}
                >
                    <PhoneDisplay
                        phone={phone}
                        hasError={Boolean(error)}
                        embedded={embedded}
                    />
                </motion.div>

                <Box
                    sx={{
                        minHeight: "20px",
                        display: "flex",
                        alignItems: "center",
                        mt: 0.3,
                    }}
                >
                    <AnimatePresence>
                        {error && (
                            <motion.div
                                initial={{ opacity: 0, y: -4 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0 }}
                            >
                                <Typography
                                    sx={{
                                        color: "#d34343",
                                        fontSize: "12px",
                                        fontWeight: 700,
                                    }}
                                >
                                    {error}
                                </Typography>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </Box>
            </Box>
        </Box>
    );
};

const PhoneDisplay = ({ phone, hasError, embedded }) => {
    return (
        <Box
            role="textbox"
            aria-label="Mobile number"
            aria-readonly="true"
            sx={{
                width: "100%",
                minHeight: {
                    xs: "54px",
                    md: embedded ? "56px" : "62px",
                },
                display: "flex",
                alignItems: "center",
                overflow: "hidden",
                borderRadius: "14px",
                backgroundColor: "#ffffff",
                border: hasError
                    ? "2px solid #e04a4a"
                    : phone.length === 10
                        ? "2px solid #25ba6c"
                        : "2px solid #dcebf2",
                boxShadow: "0 8px 22px rgba(41,102,135,0.1)",
                transition: "border-color 0.25s ease",
            }}
        >
            <Box
                sx={{
                    alignSelf: "stretch",
                    flexShrink: 0,
                    minWidth: {
                        xs: "58px",
                        md: embedded ? "64px" : "72px",
                    },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#1d4256",
                    fontSize: {
                        xs: "17px",
                        md: "19px",
                    },
                    fontWeight: 800,
                    backgroundColor: "#edf8fd",
                    borderRight: "1px solid #d9e9f0",
                }}
            >
                +91
            </Box>

            <Box
                sx={{
                    flex: 1,
                    minWidth: 0,
                    display: "flex",
                    alignItems: "center",
                    px: {
                        xs: 0.7,
                        md: 1,
                    },
                }}
            >
                {phone ? (
                    <Box
                        sx={{
                            width: "100%",
                            display: "flex",
                            alignItems: "center",
                            gap: {
                                xs: "0px",
                                sm: "2px",
                            },
                        }}
                    >
                        {Array.from({ length: 10 }).map((_, index) => (
                            <motion.div
                                key={index}
                                animate={{
                                    y: index < phone.length ? [7, 0] : 0,
                                    opacity:
                                        index < phone.length ? 1 : 0.25,
                                    scale:
                                        index < phone.length
                                            ? [0.75, 1]
                                            : 0.85,
                                }}
                                transition={{ duration: 0.18 }}
                                style={{
                                    flex: 1,
                                    minWidth: 0,
                                    textAlign: "center",
                                }}
                            >
                                <Typography
                                    sx={{
                                        color:
                                            index < phone.length
                                                ? "#17384b"
                                                : "#a4b6bf",
                                        fontSize: {
                                            xs: "16px",
                                            sm: "18px",
                                            md: embedded
                                                ? "18px"
                                                : "20px",
                                        },
                                        fontWeight: 800,
                                        lineHeight: 1,
                                    }}
                                >
                                    {phone[index] || "•"}
                                </Typography>
                            </motion.div>
                        ))}
                    </Box>
                ) : (
                    <Typography
                        sx={{
                            color: "#9cafb8",
                            fontSize: {
                                xs: "16px",
                                md: "18px",
                            },
                            fontWeight: 600,
                            letterSpacing: {
                                xs: "1px",
                                md: "2px",
                            },
                        }}
                    >
                        9***0 2****1
                    </Typography>
                )}
            </Box>

            <AnimatePresence>
                {phone.length === 10 && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.4 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.4 }}
                    >
                        <CheckCircleRounded
                            sx={{
                                color: "#24b86a",
                                fontSize: {
                                    xs: "24px",
                                    md: "26px",
                                },
                                mr: 1,
                            }}
                        />
                    </motion.div>
                )}
            </AnimatePresence>
        </Box>
    );
};

const CombinedKeypad = ({
    phone,
    isLoading,
    handleProceed,
    handleNumberClick,
    handleBackspace,
    handleClear,
    embedded,
    handleSkip,
}) => {
    return (
        <Box
            sx={{
                width: "100%",
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                p: {
                    xs: 1,
                    sm: 1.2,
                    md: 1.4,
                },
                background:
                    "linear-gradient(145deg, #62b4df 0%, #78c6e9 100%)",
                borderTop: "1px solid rgba(255,255,255,0.45)",
            }}
        >
            <Box
                sx={{
                    width: "100%",
                    flex: 1,
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                    p: {
                        xs: 0.9,
                        sm: 1,
                        md: 1.2,
                    },
                    borderRadius: {
                        xs: "16px",
                        md: "20px",
                    },
                    background: "rgba(255,255,255,0.2)",
                    border: "1px solid rgba(255,255,255,0.38)",
                    boxShadow: "0 15px 35px rgba(19,76,110,0.16)",
                    backdropFilter: "blur(16px)",
                    WebkitBackdropFilter: "blur(16px)",
                }}
            >
                <Box
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        display: "grid",
                        gridTemplateColumns:
                            "repeat(3, minmax(0, 1fr))",
                        gridTemplateRows:
                            "repeat(4, minmax(42px, 1fr))",
                        gap: {
                            xs: "6px",
                            md: embedded ? "7px" : "8px",
                        },
                    }}
                >
                    {keypadNumbers.map((number, index) => (
                        <NumberButton
                            key={number}
                            number={number}
                            index={index}
                            embedded={embedded}
                            disabled={
                                isLoading || phone.length >= 10
                            }
                            onClick={() =>
                                handleNumberClick(number)
                            }
                        />
                    ))}

                    <ActionButton
                        label="Clear"
                        embedded={embedded}
                        disabled={isLoading || !phone.length}
                        onClick={handleClear}
                    >
                        C
                    </ActionButton>

                    <NumberButton
                        number="0"
                        index={9}
                        embedded={embedded}
                        disabled={isLoading || phone.length >= 10}
                        onClick={() => handleNumberClick("0")}
                    />

                    <ActionButton
                        label="Delete"
                        embedded={embedded}
                        disabled={isLoading || !phone.length}
                        onClick={handleBackspace}
                    >
                        <BackspaceRounded />
                    </ActionButton>
                </Box>

                <Box sx={{ mt: 1.1, flexShrink: 0 }}>
                    <PrimaryButton
                        title="CONTINUE"
                        onClick={handleProceed}
                        disabled={isLoading || phone.length !== 10}
                        spinner={isLoading}
                        disabledTitle={isLoading ? "PLEASE WAIT" : undefined}
                        style={{
                            width: "100%",
                            minHeight: embedded ? "44px" : "48px",
                            borderRadius: "14px",
                            fontSize: embedded ? "15px" : "16px",
                            fontWeight: 800,
                            letterSpacing: "0.5px",
                            padding: "10px 0",
                        }}
                    />

                    <Typography
                        onClick={handleSkip}
                        sx={{
                            mt: 1.5,
                            textAlign: "center",
                            color: "#fff",
                            fontSize: "15px",
                            fontWeight: 700,
                            cursor: isLoading ? "default" : "pointer",
                            opacity: isLoading ? 0.5 : 0.95,
                            textDecoration: "underline",
                            "&:hover": {
                                opacity: 1,
                            },
                        }}
                    >
                        Skip for now
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};

const NumberButton = ({
    number,
    index,
    disabled,
    onClick,
    embedded,
}) => {
    return (
        <motion.button
            type="button"
            initial={{
                opacity: 0,
                scale: 0.7,
                y: 12,
            }}
            animate={{
                opacity: 1,
                scale: 1,
                y: 0,
            }}
            transition={{
                delay: 0.25 + index * 0.03,
                type: "spring",
                stiffness: 320,
                damping: 20,
            }}
            whileTap={
                disabled
                    ? {}
                    : {
                        scale: 0.88,
                        rotate: -2,
                    }
            }
            disabled={disabled}
            onClick={onClick}
            style={{
                width: "100%",
                height: "100%",
                minHeight: "64px",
                maxHeight: "64px",
                border: "none",
                borderRadius: embedded ? "12px" : "15px",
                color: "#173b50",
                background:
                    "linear-gradient(145deg, #ffffff 0%, #edf7fc 100%)",
                boxShadow:
                    "0 5px 0 rgba(32,100,140,0.17), 0 8px 14px rgba(32,90,124,0.15)",
                fontFamily: "inherit",
                fontSize: embedded ? "22px" : "26px",
                fontWeight: 800,
                cursor: disabled
                    ? "not-allowed"
                    : "pointer",
                opacity: disabled ? 0.55 : 1,
                WebkitTapHighlightColor:
                    "transparent",
                touchAction: "manipulation",
            }}
        >
            {number}
        </motion.button>
    );
};

const ActionButton = ({
    children,
    label,
    disabled,
    onClick,
    embedded,
}) => {
    return (
        <IconButton
            type="button"
            aria-label={label}
            disabled={disabled}
            onClick={onClick}
            sx={{
                width: "100%",
                height: "100%",
                minHeight: embedded ? "42px" : "48px",
                maxHeight: embedded ? "56px" : "64px",
                borderRadius: embedded ? "12px" : "15px",
                color: "#ffffff",
                fontSize: embedded ? "18px" : "22px",
                fontWeight: 800,
                background:
                    "rgba(27,88,123,0.32)",
                border:
                    "1px solid rgba(255,255,255,0.25)",
                boxShadow:
                    "0 5px 0 rgba(24,76,108,0.17)",
                backdropFilter: "blur(8px)",
                WebkitBackdropFilter:
                    "blur(8px)",
                touchAction: "manipulation",

                "& svg": {
                    fontSize: embedded ? "22px" : "26px",
                },

                "&:hover": {
                    background:
                        "rgba(27,88,123,0.4)",
                },

                "&:active": {
                    transform: "scale(0.88)",
                },

                "&.Mui-disabled": {
                    color:
                        "rgba(255,255,255,0.4)",
                    background:
                        "rgba(27,88,123,0.13)",
                },
            }}
        >
            {children}
        </IconButton>
    );
};

const BackgroundDecorations = () => {
    return (
        <>
            <Box
                sx={{
                    position: "absolute",
                    width: "420px",
                    height: "420px",
                    top: "-220px",
                    right: "-100px",
                    borderRadius: "50%",
                    border:
                        "70px solid rgba(255,255,255,0.08)",
                }}
            />

            <Box
                sx={{
                    position: "absolute",
                    width: "320px",
                    height: "320px",
                    left: "-170px",
                    bottom: "-130px",
                    borderRadius: "50%",
                    border:
                        "55px solid rgba(255,255,255,0.07)",
                }}
            />

            <Box
                sx={{
                    position: "absolute",
                    top: "7%",
                    left: "4%",
                    width: "28px",
                    height: "28px",
                    borderRadius: "9px",
                    transform: "rotate(30deg)",
                    background:
                        "rgba(255,255,255,0.25)",
                    animation:
                        "floatingShape 5s ease-in-out infinite",
                }}
            />
        </>
    );
};

export default PaymentProcessing;   